// src/components/admin/CompanyProfileManager.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { Country, State } from 'country-state-city';
import {
  Box, Card, CardContent, Grid, TextField, MenuItem,
  FormControl, Select, Button, Stack, Typography,
  CircularProgress, Alert,
} from '@mui/material';
import BusinessIcon from '@mui/icons-material/Business';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import ContactsIcon from '@mui/icons-material/Contacts';
import StorageIcon from '@mui/icons-material/Storage';
import SaveIcon from '@mui/icons-material/Save';

import { companyService } from '../../services/companyService';

const SectionCard = ({ icon, title, children }) => (
  <Card variant="outlined" sx={{ borderRadius: 2 }}>
    <CardContent sx={{ p: 3 }}>
      <Box display="flex" alignItems="center" gap={1} mb={3}>
        {icon}
        <Typography variant="subtitle1" fontWeight={600}>{title}</Typography>
      </Box>
      {children}
    </CardContent>
  </Card>
);

const CompanyProfileManager = ({ currentUser, showToast }) => {
  const [loading,  setLoading]  = useState(true);
  const [saving,   setSaving]   = useState(false);
  const [error,    setError]    = useState('');
  const [formData, setFormData] = useState(null);
  const [countries] = useState(Country.getAllCountries());
  const [states,    setStates]    = useState([]);

  /* ── Load own company ───────────────────────────────────────────── */
  const load = useCallback(async () => {
    const companyId = currentUser?.companyId;
    if (!companyId) {
      setError('No company associated with your account.');
      setLoading(false);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const doc = await companyService.getCompanyById(companyId);
      if (!doc) { setError('Company record not found.'); return; }
      setFormData({
        // read-only
        name:      doc.name      || '',
        companyId: doc.companyId || doc.id,
        status:    doc.status    || 'Active',
        // editable
        address1:            doc.address1            || doc.address || '',
        address2:            doc.address2            || '',
        address3:            doc.address3            || '',
        countryCode:         doc.countryCode         || 'IN',
        stateCode:           doc.stateCode           || '',
        city:                doc.city                || '',
        pincode:             doc.pincode             || '',
        phoneNumber:         doc.phoneNumber         || '',
        email:               doc.email               || '',
        website:             doc.website             || '',
        gstin:               doc.gstin               || '',
        contactPersonName:   doc.contactPersonName   || doc.contactPerson || '',
        contactPersonMobile: doc.contactPersonMobile || '',
        localSupabaseURL:     doc.localSupabaseURL     || '',
        localSupabaseAnonKey: doc.localSupabaseAnonKey || '',
      });
    } catch (e) {
      setError(e.message || 'Failed to load company profile.');
    } finally {
      setLoading(false);
    }
  }, [currentUser]);

  useEffect(() => { load(); }, [load]);

  /* ── Country → states ───────────────────────────────────────────── */
  useEffect(() => {
    if (formData?.countryCode) setStates(State.getStatesOfCountry(formData.countryCode));
    else setStates([]);
  }, [formData?.countryCode]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((p) => {
      const next = { ...p, [name]: value };
      if (name === 'countryCode') { next.stateCode = ''; next.city = ''; }
      if (name === 'stateCode')   { next.city = ''; }
      return next;
    });
  };

  const handleSave = async () => {
    if (!formData || saving) return;
    setSaving(true);
    setError('');
    try {
      // 🚫 Never send name/companyId/status — service strips them anyway,
      //    but we omit them here to keep the payload explicit.
      const {
        name, companyId, status, // eslint-disable-line no-unused-vars
        ...editable
      } = formData;

      const countryObj = countries.find((c) => c.isoCode === editable.countryCode);
      const stateObj   = states.find((s) => s.isoCode === editable.stateCode);

      const payload = {
        ...editable,
        country:       countryObj?.name || editable.countryCode,
        state:         stateObj?.name   || editable.stateCode,
        address:       editable.address1,
        contactPerson: editable.contactPersonName,
      };

      await companyService.updateCompany(currentUser.companyId, payload, currentUser);

      await companyService.logCompanyActivity(
        currentUser.companyId,
        'profile_updated',
        { by: currentUser?.email || 'unknown' },
        currentUser,
      ).catch(() => {}); // activity log is best-effort

      showToast('Company profile updated successfully.', 'success');
      await load();
    } catch (e) {
      setError(e.message || 'Failed to save company profile.');
      showToast(e.message || 'Save failed', 'error');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 4 }}>
        <CircularProgress size={20} />
        <Typography variant="body2" color="text.secondary">Loading company profile…</Typography>
      </Box>
    );
  }
  if (!formData) return <Alert severity="error">{error || 'Company profile unavailable.'}</Alert>;

  return (
    <Stack spacing={3.5}>
      {error && <Alert severity="error" sx={{ borderRadius: 2 }}>{error}</Alert>}

      {/* ── READ-ONLY IDENTITY ── */}
      <SectionCard icon={<BusinessIcon color="primary" fontSize="small" />} title="Company Identity (read-only)">
        <Grid container spacing={2.5}>
          <Grid item xs={12} sm={6} md={4}>
            <TextField 
              fullWidth 
              size="small" 
              label="Company Name"
              value={formData.name} 
              disabled 
              InputProps={{ sx: { bgcolor: 'action.hover' } }} 
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField 
              fullWidth 
              size="small" 
              label="Company Code"
              value={formData.companyId} 
              disabled 
              InputProps={{ sx: { bgcolor: 'action.hover' } }} 
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField 
              fullWidth 
              size="small" 
              label="Status"
              value={formData.status} 
              disabled 
              InputProps={{ sx: { bgcolor: 'action.hover' } }} 
            />
          </Grid>
        </Grid>
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1.5, display: 'block' }}>
          Company name, code, and status are managed by the platform administrator.
        </Typography>
      </SectionCard>

      {/* ── LOCATION ── */}
      <SectionCard icon={<LocationOnIcon color="primary" fontSize="small" />} title="Location">
        <Grid container spacing={2.5}>
          <Grid item xs={12} md={4}>
            <TextField fullWidth size="small" label="Address Line 1 *" name="address1"
              value={formData.address1} onChange={handleChange} required />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField fullWidth size="small" label="Address Line 2" name="address2"
              value={formData.address2} onChange={handleChange} />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField fullWidth size="small" label="Address Line 3" name="address3"
              value={formData.address3} onChange={handleChange} />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <Select name="countryCode" value={formData.countryCode} onChange={handleChange} displayEmpty>
                <MenuItem value="">Select Country</MenuItem>
                {countries.map((c) => <MenuItem key={c.isoCode} value={c.isoCode}>{c.name}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <Select name="stateCode" value={formData.stateCode} onChange={handleChange} displayEmpty
                disabled={!formData.countryCode}>
                <MenuItem value="">Select State</MenuItem>
                {states.map((s) => <MenuItem key={s.isoCode} value={s.isoCode}>{s.name}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField fullWidth size="small" label="City *" name="city"
              value={formData.city} onChange={handleChange} required />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField fullWidth size="small" label="Pincode / Zip *" name="pincode"
              value={formData.pincode} onChange={handleChange} inputProps={{ maxLength: 10 }} required />
          </Grid>
        </Grid>
      </SectionCard>

      {/* ── CONTACT ── */}
      <SectionCard icon={<ContactsIcon color="primary" fontSize="small" />} title="Contact & Communication">
        <Grid container spacing={2.5}>
          <Grid item xs={12} sm={6} md={3}>
            <TextField fullWidth size="small" label="Phone Number *" name="phoneNumber" type="tel"
              value={formData.phoneNumber} onChange={handleChange} required />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField fullWidth size="small" label="Email" name="email" type="email"
              value={formData.email} onChange={handleChange} />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField fullWidth size="small" label="Website" name="website"
              value={formData.website} onChange={handleChange} />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField fullWidth size="small" label="GSTIN" name="gstin"
              value={formData.gstin} onChange={handleChange} />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField fullWidth size="small" label="Contact Person Name" name="contactPersonName"
              value={formData.contactPersonName} onChange={handleChange} />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField fullWidth size="small" label="Contact Person Mobile" name="contactPersonMobile" type="tel"
              value={formData.contactPersonMobile} onChange={handleChange} />
          </Grid>
        </Grid>
      </SectionCard>

      {/* ── LOCAL INFRA ── */}
      <SectionCard icon={<StorageIcon color="primary" fontSize="small" />} title="Local Server & Database">
        <Grid container spacing={2.5}>
          <Grid item xs={12} md={4}>
            <TextField fullWidth size="small" label="Local Supabase URL" name="localSupabaseURL"
              value={formData.localSupabaseURL} onChange={handleChange}
              placeholder="http://localhost:54321 or LAN IP" />
          </Grid>
          <Grid item xs={12} md={8}>
            <TextField fullWidth size="small" label="Local Supabase Anon Key" name="localSupabaseAnonKey"
              value={formData.localSupabaseAnonKey} onChange={handleChange}
              placeholder="Public anonymous API key" />
          </Grid>
        </Grid>
      </SectionCard>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button variant="contained" disableElevation onClick={handleSave} disabled={saving}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
          sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, px: 3, minWidth: 200 }}>
          {saving ? 'Saving…' : 'Save Company Profile'}
        </Button>
      </Box>
    </Stack>
  );
};

export default CompanyProfileManager;